import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { enrollmentKeys } from './useEnrollments';

export const registrationKeys = {
  all: ['registrations'] as const,
  lists: () => [...registrationKeys.all, 'list'] as const,
  list: (params: Record<string, unknown> | string) =>
    [...registrationKeys.all, 'list', params] as const,
};

/**
 * Fetches the current user's ProgramRegistration rows.
 *
 * The api-client's `getByUser` returns the same `{ items, meta }` shape
 * as the admin `list` endpoint (see `apiClient.registrations.list`), so
 * consumers must read `data.items`. The response is passed through
 * untouched — do NOT unwrap here, so the hook stays consistent with the
 * sibling `useRegistrations` in the admin app and with the `list` hook
 * in this package.
 */
export function useRegistrations(userId?: string) {
  return useQuery({
    queryKey: registrationKeys.list(userId || ''),
    queryFn: () => apiClient.registrations.getByUser(userId || ''),
    enabled: !!userId,
  });
}

export function useRegisterProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      programId: string;
      // email + name are required by the backend CreateRegistrationDto
      // (used to find-or-create the User row). The Registration page
      // always pulls them from the auth store before invoking this hook.
      email: string;
      name: string;
      phone?: string;
      gender?: 'MALE' | 'FEMALE';
      notes?: string;
      consentGiven: boolean;
      domisili: string;
      reason: string;
    }) => apiClient.registrations.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      void queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

export function useCreateReflection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      materialId?: string;
      programId?: string;
      userId: string;
      prompt: string;
      response: string;
      isShared?: boolean;
    }) => apiClient.reflections.submit(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reflections'] });
    },
  });
}
